/**
 * Round trust seals from the print brochure („10 Jahre Speicherung“ verbatim,
 * „Revisionssicher“ in the same medal). Fixed brochure colours in both themes.
 */
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactNode } from "react";

import { H } from "../../hero-tokens";

const GOLD = "#F0DCB0";
const DISC = "#191714";
const CREAM = "#f7f4ee";
const STAR = "M0,-2.6 L0.62,-0.85 2.47,-0.8 1,0.32 1.53,2.1 0,1.05 -1.53,2.1 -1,0.32 -2.47,-0.8 -0.62,-0.85 Z";
const ARC_TOP = "M 11.3,26.2 A 12.9,12.9 0 1 1 36.7,26.2";
const ARC_BOTTOM = "M 10.14,32 A 16,16 0 0 0 37.86,32";
/** Wider top arc (220°) so a long word like REVISIONSSICHERE is not cut off. */
const ARC_TOP_WIDE = "M 10.28,29.29 A 14.6,14.6 0 1 1 37.72,29.29";
const SEAL_SIZE = 96;

/** Gold ring, dark disc, side stars, text on both arcs; `id` keeps path ids unique. */
function Medal(props: {
  readonly id: string;
  readonly topArc?: string;
  /** Fixed arc length of the top word: even spacing, ends clear of the stars. */
  readonly topLength?: number;
  readonly bottomLength?: number;
  readonly top: string;
  readonly bottom: string;
  readonly topSize: number;
  readonly bottomSize: number;
  readonly children: ReactNode;
}): ReactNode {
  const textStyle: CSSProperties = { fontFamily: H.sans };
  return (
    <svg viewBox="0 0 48 48" width={SEAL_SIZE} height={SEAL_SIZE} style={{ display: "block" }} aria-hidden="true">
      <defs>
        <path id={`${props.id}-star`} fill={GOLD} d={STAR} />
        <path id={`${props.id}-top`} d={props.topArc ?? ARC_TOP} fill="none" />
        <path id={`${props.id}-bottom`} d={ARC_BOTTOM} fill="none" />
      </defs>
      <circle cx="24" cy="24" r="19.8" fill={GOLD} />
      <circle cx="24" cy="24" r="18.4" fill={DISC} />
      {props.children}
      <text
        fontSize={props.topSize}
        fontWeight="800"
        letterSpacing={props.topLength === undefined ? "0.4" : "0"}
        fill={CREAM}
        style={textStyle}
      >
        <textPath
          href={`#${props.id}-top`}
          startOffset="50%"
          textAnchor="middle"
          textLength={props.topLength}
          lengthAdjust={props.topLength === undefined ? undefined : "spacing"}
        >
          {props.top}
        </textPath>
      </text>
      <text
        fontSize={props.bottomSize}
        fontWeight="800"
        letterSpacing={props.bottomLength === undefined ? "0.4" : "0"}
        fill={CREAM}
        style={textStyle}
      >
        <textPath
          href={`#${props.id}-bottom`}
          startOffset="50%"
          textAnchor="middle"
          textLength={props.bottomLength}
          lengthAdjust={props.bottomLength === undefined ? undefined : "spacing"}
        >
          {props.bottom}
        </textPath>
      </text>
      <use href={`#${props.id}-star`} transform="translate(9.4 24) scale(.75)" />
      <use href={`#${props.id}-star`} transform="translate(38.6 24) scale(.75)" />
    </svg>
  );
}

/** Shield + lock from the brochure's „Revisionssicher“ mark, shrunk into the medal. */
function AuditProofSeal(): ReactNode {
  const t = useTranslations("marketing.trustSeals");
  return (
    <Medal id="seal-audit" topArc={ARC_TOP_WIDE} topLength={34.5} bottomLength={27} top={t("revisionssicher")} bottom={t("archivierung")} topSize={2.9} bottomSize={3.2}>
      <g transform="translate(24 24.4) scale(0.4) translate(-24 -23.5)">
        <path
          d="M24 4 L41 8.5 V21.5 c0 11.5 -7 17.8 -17 21.5 C14 39.3 7 33 7 21.5 V8.5 Z"
          fill="none"
          stroke={CREAM}
          strokeWidth="3.2"
          strokeLinejoin="round"
        />
        <rect x="19.5" y="20" width="9" height="7.6" rx="1.4" fill="none" stroke={GOLD} strokeWidth="3" />
        <path d="M21.7 20 v-2.3 a2.3 2.3 0 0 1 4.6 0 V20" fill="none" stroke={GOLD} strokeWidth="3" strokeLinecap="round" />
      </g>
    </Medal>
  );
}

function RetentionSeal(): ReactNode {
  const t = useTranslations("marketing.trustSeals");
  return (
    <Medal id="seal-retention" top={t("jahre")} bottom={t("speicherung")} topSize={4.4} bottomSize={3.7}>
      <circle cx="24" cy="24" r="10.4" fill={GOLD} />
      <text x="24" y="27.9" textAnchor="middle" fontSize="11" fontWeight="800" fill={DISC} style={{ fontFamily: H.sans }}>
        10
      </text>
    </Medal>
  );
}

/** Dokumente module: seals hanging over the window's bottom-right edge. */
export function DokumenteTrustSeals(): ReactNode {
  const t = useTranslations("marketing.trustSeals");
  return (
    <div
      role="img"
      aria-label={t("ariaLabel")}
      style={{
        position: "absolute",
        right: 28,
        bottom: -40,
        zIndex: 35,
        display: "flex",
        gap: 12,
        filter: "drop-shadow(0 14px 18px rgba(0,0,0,0.35))",
        pointerEvents: "none",
      }}
    >
      <AuditProofSeal />
      <RetentionSeal />
    </div>
  );
}
